import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Crown } from "lucide-react";
import { Card } from "../../components/Card";
import { LoadingContainer } from "../../components/LoadingContainer";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/Table";
import colors from "@styling/colors";
import { IconSizes, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatSigned } from "../../utils/formatSigned";

const RESELLER_TOP_LIST_DISPLAY_LIMIT = 3;

const TableBox = styled(Card)`
  background-color: ${colors.background};
  margin-bottom: ${spacing.x2}px;
`;

const EmptyState = styled.div`
  padding: ${spacing.x4}px;
  text-align: center;
  color: ${colors.textMuted};
`;

const crownColorByRank: Record<1 | 2 | 3, string> = {
  1: "#D4AF37",
  2: "#A8A8A8",
  3: "#CD7F32",
};

const CrownIcon = styled(Crown) <{ $rank: 1 | 2 | 3 }>`
  color: ${({ $rank }) => crownColorByRank[$rank]};
  flex-shrink: 0;
`;

const ResellerCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const YouLabel = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.primary};
`;

const ResellerLink = styled(Link)`
  color: ${colors.primary};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const TableGapRow = styled.tr`
  background-color: ${colors.backgroundDark};

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const TableGapCell = styled.td`
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-align: center;
  border-bottom: 1px solid ${colors.border};
`;

const GrowthBreakdown = styled.span`
  margin-left: ${spacing.x1}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const AddedCount = styled.span`
  color: ${colors.successText};
`;

const RemovedCount = styled.span`
  color: ${colors.dangerText};
`;

type TopListItem = {
  rank: number;
  activeCustomers?: number;
  activeLicenses?: number;
  customerGrowth: number;
  licenseGrowth: number;
  customersAdded: number;
  customersRemoved: number;
  licensesAdded: number;
  licensesRemoved: number;
  resellerId?: string;
  resellerName?: string;
};

interface ResellerTopListTableProps {
  isLoading: boolean;
  entries: TopListItem[];
  selectedRank?: number;
  resellerId?: string;
  selfEntry?: TopListItem;
}

const getResellerViewRows = (
  entries: TopListItem[],
  selectedRank: number | undefined,
  selfEntry: TopListItem | undefined,
) => {
  const topEntries = entries.slice(0, RESELLER_TOP_LIST_DISPLAY_LIMIT);
  const showSelfBelow =
    selfEntry !== undefined &&
    selectedRank !== undefined &&
    selectedRank > RESELLER_TOP_LIST_DISPLAY_LIMIT;

  return { topEntries, showSelfBelow, selfRow: showSelfBelow ? selfEntry : undefined };
};

type GrowthCellProps = {
  growth: number;
  added: number;
  removed: number;
  title: string;
};

const GrowthCell = ({ growth, added, removed, title }: GrowthCellProps) => (
  <TableCell align="right" noWrap>
    {formatSigned(growth)}
    <GrowthBreakdown title={title}>
      <AddedCount>+{added}</AddedCount>
      {" / "}
      <RemovedCount>−{removed}</RemovedCount>
    </GrowthBreakdown>
  </TableCell>
);

type EntryRowOptions = {
  entry: TopListItem;
  selectedRank?: number;
  showActiveCounts: boolean;
  t: TFunction;
};

const EntryRow = ({
  entry,
  selectedRank,
  showActiveCounts,
  t,
}: EntryRowOptions) => {
  const isSelected = selectedRank !== undefined && entry.rank === selectedRank;

  return (
    <TableRow>
      <TableCell>
        <ResellerCellContent>
          {entry.resellerName ? (
            entry.resellerId ? (
              <ResellerLink to={`/resellers/${entry.resellerId}/overview`}>
                {entry.resellerName}
              </ResellerLink>
            ) : (
              entry.resellerName
            )
          ) : (
            t("resellerTopList.anonymousRank", { rank: entry.rank })
          )}
          {isSelected && entry.rank <= 3 && (
            <CrownIcon
              size={IconSizes.regular}
              $rank={entry.rank as 1 | 2 | 3}
              aria-hidden
            />
          )}
          {isSelected && <YouLabel>{t("resellerTopList.you")}</YouLabel>}
        </ResellerCellContent>
      </TableCell>
      {showActiveCounts && (
        <TableCell align="right">{entry.activeCustomers}</TableCell>
      )}
      {showActiveCounts && (
        <TableCell align="right">{entry.activeLicenses}</TableCell>
      )}
      <GrowthCell
        growth={entry.customerGrowth}
        added={entry.customersAdded}
        removed={entry.customersRemoved}
        title={t("resellerTopList.customerBreakdown", {
          added: entry.customersAdded,
          removed: entry.customersRemoved,
        })}
      />
      <GrowthCell
        growth={entry.licenseGrowth}
        added={entry.licensesAdded}
        removed={entry.licensesRemoved}
        title={t("resellerTopList.licenseBreakdown", {
          added: entry.licensesAdded,
          removed: entry.licensesRemoved,
        })}
      />
    </TableRow>
  );
};

export const ResellerTopListTable = ({
  isLoading,
  entries,
  selectedRank,
  resellerId,
  selfEntry,
}: ResellerTopListTableProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <TableBox>
        <LoadingContainer message={t("statistics.loading")} />
      </TableBox>
    );
  }

  if (entries.length === 0) {
    return (
      <TableBox>
        <EmptyState>{t("resellerTopList.empty")}</EmptyState>
      </TableBox>
    );
  }

  const showActiveCounts = !resellerId;
  const columnCount = showActiveCounts ? 5 : 3;

  const { topEntries, showSelfBelow, selfRow } = resellerId
    ? getResellerViewRows(entries, selectedRank, selfEntry)
    : {
      topEntries: entries,
      showSelfBelow: false,
      selfRow: undefined,
    };

  return (
    <TableBox>
      <Table minWidth={showActiveCounts ? "700px" : "500px"}>
        <TableHeader>
          <tr>
            <TableHeaderCell>{t("resellerTopList.reseller")}</TableHeaderCell>
            {showActiveCounts && (
              <TableHeaderCell align="right">
                {t("resellerTopList.activeCustomers")}
              </TableHeaderCell>
            )}
            {showActiveCounts && (
              <TableHeaderCell align="right">
                {t("resellerTopList.activeLicenses")}
              </TableHeaderCell>
            )}
            <TableHeaderCell align="right">
              {t("resellerTopList.customerGrowth")}
            </TableHeaderCell>
            <TableHeaderCell align="right">
              {t("resellerTopList.licenseGrowth")}
            </TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {topEntries.map((entry) =>
            <EntryRow key={entry.resellerId ?? String(entry.rank)} entry={entry}  showActiveCounts={showActiveCounts} t={t} selectedRank={selectedRank} />
          )}
          {showSelfBelow && (
            <TableGapRow>
              <TableGapCell
                colSpan={columnCount}
                role="separator"
              >
                ...
              </TableGapCell>
            </TableGapRow>
          )}
          {selfRow &&
            <EntryRow key={`self-${selfRow.rank}`} entry={selfRow} showActiveCounts={showActiveCounts} t={t} selectedRank={selectedRank} />
          }
        </TableBody>
      </Table>
    </TableBox>
  );
};
